export type DeploymentSlot = "live" | "staging";

// startup.sh substitutes these into index.html from the SLOT_NAME app setting and
// build-id.txt, so they describe the instance that served the page.
const SLOT_NAME_META = "x-slot-name";
const BUILD_ID_META = "x-build-id";
const SLOT_NAME_PLACEHOLDER = "__slot_name__";
const BUILD_ID_PLACEHOLDER = "__build_id__";
const PRODUCTION_SLOT_NAME = "production";

export const resolveSlotName = (slotName: string | null): DeploymentSlot | null => {
    const normalized = slotName?.trim().toLowerCase();

    // Unsubstituted placeholder, or the fallback startup.sh writes when the slot
    // app setting is missing. Either way the slot is unknown.
    if (!normalized || normalized === SLOT_NAME_PLACEHOLDER || normalized === "unknown") {
        return null;
    }

    return normalized === PRODUCTION_SLOT_NAME ? "live" : "staging";
};

const readMetaContent = (name: string): string | null => {
    return document.querySelector(`meta[name="${name}"]`)?.getAttribute("content") ?? null;
};

const readSlotNameMeta = (): string | null => readMetaContent(SLOT_NAME_META);

const readBuildIdMeta = (): string | null => readMetaContent(BUILD_ID_META);

export const resolveBuildId = (buildId: string | null): string | undefined => {
    const trimmed = buildId?.trim();
    const normalized = trimmed?.toLowerCase();

    if (!trimmed || normalized === BUILD_ID_PLACEHOLDER || normalized === "unknown") {
        return undefined;
    }

    return trimmed;
};

export const getInjectedBuildId = (): string | undefined => {
    return resolveBuildId(readBuildIdMeta());
};

export const resolveDeploymentSlot = (slotName: string | null): DeploymentSlot => {
    // Unknown slot (missing meta, unsubstituted placeholder) is live: a false
    // staging banner on production is worse than a missing banner for testers.
    return resolveSlotName(slotName) ?? "live";
};

export const getDeploymentSlot = (): DeploymentSlot => {
    return resolveDeploymentSlot(readSlotNameMeta());
};
